import React, { useEffect, useState } from "react";
import {
  Modal,
  Header,
  Loader,
  Tab,
  Grid,
  Statistic,
  Message,
  Icon,
  Button,
  Divider,
  Label,
  List,
} from "semantic-ui-react";
import { AdminUsersV2 } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { UsersV2RoleTag } from "../UsersV2RoleTag";

const usersV2Api = new AdminUsersV2();

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("es-ES");
  } catch {
    return "—";
  }
}

function userCreatedAt(u) {
  return u?.created_at || u?.createdAt;
}

function userUpdatedAt(u) {
  return u?.updated_at || u?.updatedAt;
}

function HeaderAvatar({ src }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <Icon
        name="user circle"
        size="big"
        style={{ color: "#bdbdbd", margin: 0 }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={() => setFailed(true)}
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
  );
}

function fullName(u) {
  if (!u) return "—";
  const parts = [u.firstname || u.name, u.lastname || u.surname].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(" ") : u.email || "—";
}

function InfoTab({ user }) {
  return (
    <Tab.Pane>
      <Grid columns={2} stackable>
        <Grid.Column>
          <Header sub>Email</Header>
          <p>{user.email || "—"}</p>
          <Header sub>Nombre</Header>
          <p>
            {user.firstname || "—"} {user.lastname || ""}
          </p>
          <Header sub>Telefono</Header>
          <p>{user.phone || "—"}</p>
          <Header sub>Pais / direccion</Header>
          <p>
            {user.country || "—"}
            {user.address ? ` — ${user.address}` : ""}
          </p>
        </Grid.Column>
        <Grid.Column>
          <Header sub>Rol primario</Header>
          <p>
            <UsersV2RoleTag role={user.role} size="medium" />
          </p>
          <Header sub>Estado</Header>
          <p>
            {user.active ? (
              <span style={{ color: "#21ba45" }}>
                <Icon name="check circle" /> Activo
              </span>
            ) : (
              <span style={{ color: "#999" }}>
                <Icon name="ban" /> Inactivo
              </span>
            )}
          </p>
          <Header sub>Creado</Header>
          <p>{formatDateTime(userCreatedAt(user))}</p>
          <Header sub>Modificado</Header>
          <p>{formatDateTime(userUpdatedAt(user))}</p>
        </Grid.Column>
      </Grid>
    </Tab.Pane>
  );
}

function RolesTab({ user }) {
  return (
    <Tab.Pane>
      <Header as="h4">Roles derivados (multi-rol)</Header>
      <p style={{ color: "#666", fontSize: 13 }}>
        Los roles se calculan a partir de las refs populadas (teacher, student,
        therapists[], patients[], institutionSimulator, etc.). El primer rol es
        el primario almacenado en User; los siguientes son derivados.
      </p>
      <div style={{ marginBottom: 16 }}>
        {(user.roles || []).map((r) => (
          <UsersV2RoleTag key={r} role={r} size="medium" />
        ))}
      </div>

      {user.isOrphan && (
        <Message warning icon>
          <Icon name="warning sign" />
          <Message.Content>
            <Message.Header>Usuario huerfano</Message.Header>
            Su rol primario implica una entidad populada que no existe (eg.
            role=teacher pero no hay Teacher asociado). Considera el modo
            DEACTIVATE o limpieza manual.
          </Message.Content>
        </Message>
      )}

      <Divider />

      <Header as="h4">Refs vinculadas</Header>
      <List relaxed>
        {user.teacher && (
          <List.Item>
            <Label color="yellow">Profesor</Label> {fullName(user.teacher)}{" "}
            {user.teacher.institution && (
              <span style={{ color: "#888" }}>
                · institucion {String(user.teacher.institution)}
              </span>
            )}
          </List.Item>
        )}
        {user.student && (
          <List.Item>
            <Label color="orange">Alumno</Label> {fullName(user.student)}{" "}
            {user.student.classnumber && (
              <span style={{ color: "#888" }}>
                · clase {user.student.classnumber}
              </span>
            )}
          </List.Item>
        )}
        {user.partner && (
          <List.Item>
            <Label color="green">Partner padre</Label> {fullName(user.partner)}
          </List.Item>
        )}
        {Array.isArray(user.therapists) && user.therapists.length > 0 && (
          <List.Item>
            <Label color="purple">Terapeutas ({user.therapists.length})</Label>
            <List.List>
              {user.therapists.map((t) => (
                <List.Item key={t._id || String(t)}>
                  {fullName(t)}{" "}
                  {t.email && (
                    <span style={{ color: "#888" }}>· {t.email}</span>
                  )}
                </List.Item>
              ))}
            </List.List>
          </List.Item>
        )}
        {Array.isArray(user.patients) && user.patients.length > 0 && (
          <List.Item>
            <Label color="violet">Pacientes ({user.patients.length})</Label>
            <List.List>
              {user.patients.map((p) => (
                <List.Item key={p._id || String(p)}>{fullName(p)}</List.Item>
              ))}
            </List.List>
          </List.Item>
        )}
        {Array.isArray(user.institutions) && user.institutions.length > 0 && (
          <List.Item>
            <Label color="yellow">
              Instituciones ({user.institutions.length})
            </Label>
            <List.List>
              {user.institutions.map((i) => (
                <List.Item key={i._id || String(i)}>
                  {i.institutionnam || i.name || String(i._id || i)}
                </List.Item>
              ))}
            </List.List>
          </List.Item>
        )}
        {user.institutionSimulator && (
          <List.Item>
            <Label color="blue">Institucion simulador</Label>{" "}
            {user.institutionSimulator.name ||
              String(user.institutionSimulator)}
          </List.Item>
        )}
        {user.planEduca && (
          <List.Item>
            <Label color="teal">Plan Educa</Label>{" "}
            {user.planEduca.name || String(user.planEduca)}
          </List.Item>
        )}
      </List>
    </Tab.Pane>
  );
}

function CountsTab({ counts }) {
  if (!counts) return <Tab.Pane>Sin datos.</Tab.Pane>;
  const items = [
    { label: "Instituciones", value: counts.institutions },
    { label: "Terapeutas", value: counts.therapists },
    { label: "Pacientes", value: counts.patients },
    { label: "Facturaciones", value: counts.billings },
    { label: "Tickets soporte", value: counts.supportTickets },
    { label: "Activaciones plan", value: counts.planActivations },
  ];
  if (counts.evaluationsAsTherapist !== undefined) {
    items.push({
      label: "Evals como terapeuta",
      value: counts.evaluationsAsTherapist,
    });
  }
  if (counts.sessionsAsTherapist !== undefined) {
    items.push({
      label: "Sesiones como terapeuta",
      value: counts.sessionsAsTherapist,
    });
  }
  if (counts.evaluationsAsPatient !== undefined) {
    items.push({
      label: "Evals como paciente",
      value: counts.evaluationsAsPatient,
    });
  }
  return (
    <Tab.Pane>
      <Statistic.Group widths="three" size="tiny">
        {items.map((it) => (
          <Statistic key={it.label}>
            <Statistic.Value>{it.value || 0}</Statistic.Value>
            <Statistic.Label>{it.label}</Statistic.Label>
          </Statistic>
        ))}
      </Statistic.Group>
    </Tab.Pane>
  );
}

/**
 * Drawer 360 lateral — implementado como Modal de Semantic UI con tamano `large`
 * para mantener consistencia visual con el resto del admin (que ya usa
 * BasicModal). Se podria migrar a un drawer real de Tailwind en el futuro
 * sin cambiar la API publica del componente.
 */
export function UsersV2Drawer({ userId, open, onClose, onDelete, onEdit }) {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!open || !userId) {
      setUser(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await usersV2Api.detail(accessToken, userId);
        if (!cancelled) setUser(data.user);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Error cargando el detalle.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId, accessToken]);

  const panes = user
    ? [
        {
          menuItem: { key: "info", icon: "user", content: "Info" },
          render: () => <InfoTab user={user} />,
        },
        {
          menuItem: { key: "roles", icon: "tags", content: "Roles" },
          render: () => <RolesTab user={user} />,
        },
        {
          menuItem: { key: "counts", icon: "chart bar", content: "Conteos" },
          render: () => <CountsTab counts={user.counts} />,
        },
      ]
    : [];

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        {user ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <HeaderAvatar src={user.avatar} />
            <div>
              <div>{fullName(user)}</div>
              <div
                style={{ fontSize: 13, fontWeight: "normal", color: "#666" }}
              >
                {user.email}
              </div>
            </div>
          </div>
        ) : (
          "Cargando perfil..."
        )}
      </Modal.Header>
      <Modal.Content scrolling>
        {loading && (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        )}
        {error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}
        {!loading && user && <Tab panes={panes} />}
      </Modal.Content>
      <Modal.Actions>
        {user && (
          <>
            <Button
              color="blue"
              basic
              icon
              labelPosition="left"
              onClick={() => onEdit && onEdit(user)}
            >
              <Icon name="edit" />
              Editar
            </Button>
            <Button
              color="red"
              basic
              icon
              labelPosition="left"
              onClick={() => onDelete && onDelete(user)}
            >
              <Icon name="trash" />
              Borrar / desactivar
            </Button>
          </>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </Modal.Actions>
    </Modal>
  );
}
